import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  withRouter,
} from 'react-router-dom';

import { Toast } from '@vivaahealth/design-system';

import AddInsurancePage from './AddInsurancePage/AddInsurancePage';
import EditInsurancePage from './EditInsurancePage/EditInsurancePage';
import ProcessingInsurancePage from './Processing/ProcessingInsurancePage';
import RteErrorReviewPage from './Processing/RteErrorReviewPage';
import VerifyingInsurancePage from './Processing/VerifyingInsurancePage';
import ReviewInsurancePage from './ReviewInsurancePage/ReviewInsurancePage';
import UploadCard from '../../UploadCardContainer/components/UploadCard/UploadCard';
import {
  INITIAL_OCR_STATE,
  INITIAL_RTE_STATE,
  OcrState,
  MEDICARE_PAYORS,
  RteState,
  UpdateInsuranceFormValue,
} from './types';
import { PictureState } from '../../UploadCardContainer/types';
import {
  parseFieldValue,
  getSelfSubscriberId,
  getSubscribersById,
  getCleanSubscribers,
} from './utils';

import { sendToOcr } from '../../UploadDocumentPage/utils';

import * as api from '../../../../api';
import * as logger from '../../../../logger';
import Loader from '../../../../components/Loader';
import { makeFetchInsuranceCards } from '../../../../reducers/patient';
import * as store from '../../../../reducers/store';
import { Side } from '../../../../utils/enum';
import {
  getSelectedForms,
  getSelectedPracticePreference,
} from '../../../../utils/formSet';
import { sendEventMessage, Message } from '../../../../utils/native';
import SubscriberPage from './SubscriberPage/SubscriberPage';
import { getCoverageStartDate, getDate } from './EditInsurancePage/utils';
import { maybeUpdateInsuranceDetails } from '../MultiInsurance/utils';

interface StateProps {
  forms: store.Forms;
  cards: store.Loaded['insuranceCards']['data'];
  patientAppRteEnabled: store.All['masterPracticePreference']['patientAppRteEnabled'];
  exportInsuranceWithNullCoverageStartDateEnabled: store.All['masterPracticePreference']['exportInsuranceWithNullCoverageStartDateEnabled'];
  selectedFormSetId: store.FormSetSelected['selectedFormSetId'];
}

interface DispatchProps {
  fetchInsuranceCards: (previsitFormSetId: string) => Promise<void>;
}

interface InsuranceRouterProps
  extends StateProps,
    DispatchProps,
    RouteComponentProps,
    WithTranslation {
  encounter: api.Encounter.Encounter;
  field: api.PrevisitFormField.PrevisitFormFieldWithConfig;
  navigateToNextPage: () => Promise<void>;
  setFormField: (formFieldId: string, value: string) => void;
}

const InsuranceRouter = ({
  forms,
  cards,
  encounter,
  fetchInsuranceCards,
  field,
  history,
  location,
  match,
  navigateToNextPage,
  patientAppRteEnabled,
  selectedFormSetId,
  exportInsuranceWithNullCoverageStartDateEnabled,
  setFormField,
  t,
}: InsuranceRouterProps) => {
  const [loaded, setLoaded] = useState(false);
  const [payorsDisplayNameMapping, setPayorsDisplayNameMapping] = useState<
    Record<string, string>
  >({});
  const [pictures, setPictures] = useState<PictureState>({
    [Side.Front]: null,
    [Side.Back]: null,
  });
  const [ocrState, setOcrState] = useState<OcrState>(INITIAL_OCR_STATE);
  const [rteState, setRteState] = useState<RteState>(INITIAL_RTE_STATE);
  const [patientInsuranceInformationId, setPatientInsuranceInformationId] =
    useState<string | undefined>();
  const [patientUploadId, setPatientUploadId] = useState<string | undefined>();
  const toast = Toast.useToast();
  const isRequired = field.required;
  const fieldValue = parseFieldValue(field.value);

  const updateInsuranceFormValue = async (
    newValues: UpdateInsuranceFormValue,
  ) => {
    const currentValue = fieldValue;

    const stringValue = JSON.stringify({
      ...currentValue,
      ...newValues,
    });
    setFormField(field.id, stringValue);
    try {
      await api.client.Previsit.updateFormField(
        field.previsitFormSetId,
        field.id,
        stringValue,
      );
    } catch (err) {
      logger.error(err, 'ran into error trying to update field value');
    }
    sendEventMessage(Message.ActiveAction);
  };

  const addInsuranceId = async (
    footprint: api.PrevisitPatientInsurance.Footprint,
    insuranceCoverage: api.PrevisitPatientInsurance.InsuranceCoverage,
  ) => {
    const value = fieldValue;

    const addedInsuranceIds = [
      ...(value.addedInsuranceIds ?? []),
      insuranceCoverage.id,
    ];
    const uniqueAddedInsuranceIds = [...new Set(addedInsuranceIds)];

    const otherExistingInsuranceCoverages = (
      value?.insuranceCoverages || []
    ).filter((insurance) => insurance.id !== insuranceCoverage.id);

    await updateInsuranceFormValue({
      addedInsuranceIds: uniqueAddedInsuranceIds,
      unverifiedInsuranceIds:
        value.unverifiedInsuranceIds?.filter(
          (id) => id !== insuranceCoverage.id,
        ) || [],
      insuranceCoverages: [
        ...otherExistingInsuranceCoverages,
        {
          id: insuranceCoverage.id,
          payor: insuranceCoverage.payor,
          primary: false,
          primaryPolicyHolderMemberId:
            insuranceCoverage.primaryPolicyHolderMemberId,
          groupNumber: insuranceCoverage.groupNumber,
          coverageStartDate: getCoverageStartDate(
            exportInsuranceWithNullCoverageStartDateEnabled,
            insuranceCoverage.coverageStartDate,
          ),
          subscriberId: insuranceCoverage.subscriberId,
        },
      ],
      footprint,
    });
  };

  const handleOcr = async (uploadId: string, side: Side, payor?: string) => {
    const ocrResults = await sendToOcr(uploadId, side, payor);
    if (ocrResults && side === Side.Front) {
      setOcrState((prev) => ({ ...prev, results: ocrResults }));
    }

    if (side === Side.Front) {
      await updateInsuranceFormValue({
        footprint: api.PrevisitPatientInsurance.Footprint.UploadedFront,
      });
    } else {
      await updateInsuranceFormValue({
        footprint: api.PrevisitPatientInsurance.Footprint.UploadedBack,
      });
    }

    return ocrResults;
  };

  const submitEditInsurance = async (
    values: api.PrevisitPatientInsurance.InsuranceCoverage,
  ) => {
    setOcrState(INITIAL_OCR_STATE);

    await updateInsuranceFormValue({
      footprint: api.PrevisitPatientInsurance.Footprint.AnsweredEditContinue,
    });
    await fetchInsuranceCards(selectedFormSetId);

    let path: string;
    if (patientAppRteEnabled) {
      setRteState(INITIAL_RTE_STATE);
      path = '../verify';
    } else {
      if (values.payor && MEDICARE_PAYORS.includes(values.payor)) {
        const selfSubscriberId = getSelfSubscriberId(field.value, forms);
        await addInsuranceId(
          api.PrevisitPatientInsurance.Footprint.SubmittedSubscriberDetails,
          { ...values, subscriberId: selfSubscriberId },
        );

        toast.open(t('toast'), { autoHideDuration: 5000 });
        path = '../review';
      } else {
        path = './subscriber';
      }
    }
    history.replace(path + location.search);
  };

  const navigateToEditPage = (insuranceInformationId: string) =>
    history.replace(`./${insuranceInformationId}/edit` + location.search);

  const handleRteResponse = async (
    patientInsuranceInformationId: string,
    patientUploadId: string,
    rteState: RteState,
  ) => {
    const replaceViewAfterTimeout = (path: string) => {
      setTimeout(() => history.replace(path + location.search), 500);
    };

    const handleExportableInsurance = async (args: {
      insuranceInformationId: string;
      rteResults: api.PatientRealTimeEligibility.RealTimeEligibilityResults;
    }) => {
      const { insuranceInformationId, rteResults } = args;
      const isSelfSubscriber =
        rteResults.patientRelationshipToSubscriber ===
        api.Common.Relationship.Self;

      let subscriber:
        | api.PrevisitPatientInsurance.PrevisitSubscriber
        | undefined;
      if (isSelfSubscriber) {
        const subscribersById = getSubscribersById(field.value, forms);
        subscriber = _.find(
          subscribersById,
          (s) =>
            s.patientRelationshipToPrimaryPolicyholder ===
            api.Common.Relationship.Self,
        );
      }
      const coverageStartDate = rteResults.coverageStartDate;
      const groupNumber = rteResults.groupNumber;
      // We don't always have this information for inconclusive RTE results
      if (!(isSelfSubscriber || coverageStartDate || groupNumber)) {
        return;
      }

      const updatedValues = {
        ...(isSelfSubscriber && {
          ..._.mapValues(
            _.omitBy(
              _.omit(subscriber, [
                'id',
                'newlyAdded',
                'primaryPolicyHolderMemberId',
                'primaryPolicyHolderAddressId',
              ]),
              _.isNil,
            ),
            (value) => {
              if (_.isObject(value)) {
                return value;
              }
              return value === '' ? null : value;
            },
          ),
        }),
        ...(coverageStartDate && {
          coverageStartDate: getCoverageStartDate(
            exportInsuranceWithNullCoverageStartDateEnabled,
            coverageStartDate,
          ),
        }),
        ...(groupNumber && {
          groupNumber,
        }),
      };
      try {
        if (!_.isEmpty(updatedValues)) {
          await api.client.PrevisitPatientInsuranceInformation.update({
            insuranceInformationId,
            values: updatedValues,
            encounterId: encounter.id,
          });
        }
      } catch (err) {
        logger.error(
          err,
          'Failed to update insurance information due to error',
        );
      }
    };

    if (!rteState.results) {
      replaceViewAfterTimeout(`./${patientInsuranceInformationId}/edit`);
      return;
    }
    const { status } = rteState.results;

    let footprint: api.PrevisitPatientInsurance.Footprint | undefined;

    switch (status) {
      case api.PatientRealTimeEligibility.EligibilityResponse.Active:
        await handleExportableInsurance({
          insuranceInformationId: patientInsuranceInformationId,
          rteResults: rteState.results,
        });
        footprint =
          api.PrevisitPatientInsurance.Footprint.VerifiedActiveWithRte;
        break;
      case api.PatientRealTimeEligibility.EligibilityResponse.NotFound:
        await handleExportableInsurance({
          insuranceInformationId: patientInsuranceInformationId,
          rteResults: rteState.results,
        });
        footprint =
          api.PrevisitPatientInsurance.Footprint.InsuranceNotFoundWithRte;
        break;
      case api.PatientRealTimeEligibility.EligibilityResponse.PayorNotSupported:
        await handleExportableInsurance({
          insuranceInformationId: patientInsuranceInformationId,
          rteResults: rteState.results,
        });
        footprint =
          api.PrevisitPatientInsurance.Footprint.PayorNotSupportedForRte;
        break;
      case api.PatientRealTimeEligibility.EligibilityResponse.Error:
        await handleExportableInsurance({
          insuranceInformationId: patientInsuranceInformationId,
          rteResults: rteState.results,
        });
        footprint = api.PrevisitPatientInsurance.Footprint.RteError;
        break;
      case api.PatientRealTimeEligibility.EligibilityResponse.Inactive:
        const currentValue = fieldValue;
        const existingUnverifiedInsuranceIds = [
          ...(currentValue.unverifiedInsuranceIds || []),
          patientInsuranceInformationId,
        ];
        const unverifiedInsuranceIds = [
          ...new Set(existingUnverifiedInsuranceIds),
        ];
        await updateInsuranceFormValue({
          unverifiedInsuranceIds,
          footprint: api.PrevisitPatientInsurance.Footprint.InactiveRteResponse,
        });
        await api.client.PatientUpload.invalidateCurrentDocument(
          patientUploadId,
        );
        await api.client.PrevisitPatientInsuranceInformation.remove({
          insuranceInformationId: patientInsuranceInformationId,
          encounterId: encounter.id,
        });
        replaceViewAfterTimeout('./error');
        return;
    }

    await fetchInsuranceCards(selectedFormSetId);
    if (
      rteState.results.payor &&
      MEDICARE_PAYORS.includes(rteState.results.payor)
    ) {
      const insuranceCoverage = cards.find(
        (card) => card.id === patientInsuranceInformationId,
      )!;
      const selfSubscriberId = getSelfSubscriberId(field.value, forms);
      await addInsuranceId(footprint, {
        id: insuranceCoverage.id,
        payor: insuranceCoverage.payor,
        primary: false,
        primaryPolicyHolderMemberId:
          insuranceCoverage.primaryPolicyHolderMemberId,
        groupNumber: insuranceCoverage.groupNumber,
        coverageStartDate: getCoverageStartDate(
          exportInsuranceWithNullCoverageStartDateEnabled,
          insuranceCoverage.coverageStartDate,
        ),
        subscriberId: selfSubscriberId,
      });
      toast.open(t('toast'), { autoHideDuration: 5000 });
      replaceViewAfterTimeout('./review');
    } else {
      await updateInsuranceFormValue({ footprint });
      replaceViewAfterTimeout(`./${patientInsuranceInformationId}/subscriber`);
    }
  };

  const handleRemoveInsurance = async (cardId: string) => {
    const card = cards.find((card) => card.id === cardId);

    if (!card) {
      logger.error(new Error(`Can't find card with id: ${cardId}`));
      return;
    }

    try {
      await api.client.PrevisitPatientInsuranceInformation.remove({
        insuranceInformationId: card.id,
        encounterId: encounter.id,
      });
    } catch (err) {
      logger.error(new Error(`Failed to remove card with id: ${cardId}`));
    }

    const uploadId = card.patientUploadId;
    try {
      if (uploadId) {
        await api.client.PatientUpload.invalidateCurrentDocument(uploadId);
      }
    } catch (err) {
      logger.error(err, `failed to invalidate document with id of ${uploadId}`);
    }

    const currentValue = fieldValue;
    const invalidInsuranceIds = [
      ...(currentValue.invalidInsuranceIds || []),
      cardId,
    ];
    const uniqueInvalidInsuranceIds = [...new Set(invalidInsuranceIds)];
    await updateInsuranceFormValue({
      insuranceCoverages: (currentValue.insuranceCoverages || []).filter(
        (insurance) => insurance.id !== cardId,
      ),
      addedInsuranceIds:
        currentValue.addedInsuranceIds?.filter((id) => id !== cardId) || [],
      unverifiedInsuranceIds:
        currentValue.unverifiedInsuranceIds?.filter((id) => id !== cardId) ||
        [],
      confirmedInsuranceIds:
        currentValue.confirmedInsuranceIds?.filter((id) => id !== cardId) || [],
      ...(!currentValue.addedInsuranceIds?.includes(cardId) && {
        invalidInsuranceIds: uniqueInvalidInsuranceIds, // invalidate if insurance was ingested (e.g. not in added)
      }),
      footprint: api.PrevisitPatientInsurance.Footprint.RemovedInsurance,
    });

    await fetchInsuranceCards(selectedFormSetId);
  };

  const handleInsuranceChoice = async (
    payingWithInsurance: boolean,
    skippingInsurance: boolean,
  ) => {
    if (skippingInsurance) {
      await updateInsuranceFormValue({
        skippedInsurance: true,
        payingWithInsurance: true,
        footprint: api.PrevisitPatientInsurance.Footprint.SkippedInsurance,
      });
      return;
    }

    await updateInsuranceFormValue({
      payingWithInsurance,
      skippedInsurance: false,
      footprint: payingWithInsurance
        ? api.PrevisitPatientInsurance.Footprint.AnsweredPayingWithInsurance
        : api.PrevisitPatientInsurance.Footprint.AnsweredPayingWithoutInsurance,
    });
  };

  const submitSubscriberForm = async (
    subscribers: api.PrevisitPatientInsurance.PrevisitSubscriber[],
    subscriberId: string,
    insuranceInformationId: string,
  ) => {
    const currentValue = parseFieldValue(field.value);
    const updatedInsuranceIds = [
      ...(currentValue.addedInsuranceIds ? currentValue.addedInsuranceIds : []),
      insuranceInformationId,
    ];
    const uniqueIds = [...new Set(updatedInsuranceIds)];
    const insuranceCoverage = cards.find(
      (card) => card.id === insuranceInformationId,
    )!;
    const alreadyExists = currentValue.insuranceCoverages?.some(
      (insurance) => insurance.id === insuranceCoverage.id,
    );

    await updateInsuranceFormValue({
      addedInsuranceIds: uniqueIds,
      unverifiedInsuranceIds:
        currentValue.unverifiedInsuranceIds?.filter(
          (id) => id !== insuranceInformationId,
        ) || [],
      subscribers,
      ...(!alreadyExists && {
        insuranceCoverages: [
          ...(currentValue.insuranceCoverages || []),
          {
            id: insuranceCoverage.id,
            payor: insuranceCoverage.payor,
            primary: false,
            primaryPolicyHolderMemberId:
              insuranceCoverage.primaryPolicyHolderMemberId,
            groupNumber: insuranceCoverage.groupNumber,
            coverageStartDate: getCoverageStartDate(
              exportInsuranceWithNullCoverageStartDateEnabled,
              insuranceCoverage.coverageStartDate,
            ),
            subscriberId,
          },
        ],
      }),
      footprint:
        api.PrevisitPatientInsurance.Footprint.SubmittedSubscriberDetails,
    });

    await fetchInsuranceCards(selectedFormSetId);
    toast.open(t('toast'), { autoHideDuration: 5000 });
    history.replace('../review' + location.search);
  };

  const addConfirmedInsurances = async () => {
    const currentValue = parseFieldValue(field.value);

    const confirmedInsurance: string[] = [
      ...(currentValue.confirmedInsuranceIds ?? []),
      ...cards
        .map((card) => card.id)
        .filter(
          (cardId) =>
            cardId !== null &&
            !(currentValue.addedInsuranceIds ?? []).includes(cardId),
        ),
    ];
    const uniqueConfirmedInsurance = [...new Set(confirmedInsurance)];

    const insuranceCoveragesById = _.mapKeys(
      currentValue?.insuranceCoverages || [],
      'id',
    );
    const subscribers = currentValue.subscribers;

    await updateInsuranceFormValue({
      payingWithInsurance: true,
      confirmedInsuranceIds: uniqueConfirmedInsurance,
      insuranceCoverages: cards.map((card) => {
        let subscriberId: string | undefined;
        if (!insuranceCoveragesById[card.id]) {
          const matchingSubscriber = subscribers.find((subscriber) => {
            const fieldsToCompare = [
              'primaryPolicyHolderFirstName',
              'primaryPolicyHolderLastName',
            ];

            return _.isEqual(
              {
                ..._.pick(subscriber, fieldsToCompare),
                primaryPolicyHolderDob: getDate(
                  subscriber?.primaryPolicyHolderDob,
                ),
              },
              {
                ..._.pick(card, fieldsToCompare),
                primaryPolicyHolderDob: getDate(card?.primaryPolicyHolderDob),
              },
            );
          });
          subscriberId = matchingSubscriber?.id || card.id;
        } else {
          subscriberId = insuranceCoveragesById[card.id].subscriberId!;
        }

        return {
          id: card.id,
          payor: card.payor,
          primary: false,
          primaryPolicyHolderMemberId:
            card.primaryPolicyHolderMemberId || card.memberId,
          groupNumber: card.groupNumber,
          coverageStartDate: getCoverageStartDate(
            exportInsuranceWithNullCoverageStartDateEnabled,
            card.coverageStartDate,
          ),
          subscriberId,
        };
      }),
      footprint: api.PrevisitPatientInsurance.Footprint.ConfirmedInsurance,
    });
  };

  const loadPayors = useCallback(async () => {
    const mapping = await api.client.PrevisitPayor.findAll(encounter.id);
    setPayorsDisplayNameMapping(mapping);
  }, [encounter]);

  useEffect(() => {
    loadPayors();
  }, [loadPayors, encounter]);

  useEffect(() => {
    fetchInsuranceCards(selectedFormSetId).then(() => setLoaded(true));
  }, [fetchInsuranceCards, selectedFormSetId]);

  if (!loaded) {
    return <Loader />;
  }

  return (
    <Switch>
      <Redirect
        from="/:url*(/+)" // prevents trailing slash, which interferes with routing
        to={location.pathname.slice(0, -1) + location.search}
      />
      <Route exact path={`${match.path}/`}>
        <AddInsurancePage
          isRequired={isRequired}
          cards={cards}
          navigateToNextPage={navigateToNextPage}
          handleInsuranceChoice={handleInsuranceChoice}
          onAddNewCard={async () => {
            history.push(`${match.url}/upload` + location.search);
          }}
        />
      </Route>
      <Route path={`${match.path}/upload`}>
        <UploadCard
          onContinue={async (pictures: PictureState) => {
            await handleInsuranceChoice(true, false);
            setPictures(pictures);
            history.replace('./processing' + location.search);
          }}
        />
      </Route>
      <Route path={`${match.path}/processing`}>
        <ProcessingInsurancePage
          encounter={encounter}
          pictures={pictures}
          sendToOCR={handleOcr}
          onComplete={async (
            patientInsuranceInformationId?: string,
            ocrResults?: api.InsuranceCardOcrResult.InsuranceCardOcrResult,
          ) => {
            if (!patientInsuranceInformationId) {
              return;
            }

            await fetchInsuranceCards(selectedFormSetId);

            const needToEdit =
              !ocrResults?.insuranceProviderName ||
              ocrResults.insuranceProviderName ===
                api.PatientInsuranceInformation.Payor.Other ||
              !ocrResults?.memberId;
            let propagateOcrState = needToEdit;
            if (needToEdit) {
              navigateToEditPage(patientInsuranceInformationId);
            } else if (patientAppRteEnabled) {
              setRteState(INITIAL_RTE_STATE);
              propagateOcrState = true;
              history.replace('./verify' + location.search);
            } else {
              if (MEDICARE_PAYORS.includes(ocrResults.insuranceProviderName!)) {
                const parsedValue = parseFieldValue(field.value);
                const selfSubscriberId = getSelfSubscriberId(
                  field.value,
                  forms,
                );

                const insuranceCoverage: api.PrevisitPatientInsurance.InsuranceCoverage =
                  {
                    coverageStartDate: getCoverageStartDate(
                      exportInsuranceWithNullCoverageStartDateEnabled,
                      ocrResults?.coverageStartDate,
                    ),
                    groupNumber: ocrResults.groupNumber ?? '',
                    id: patientInsuranceInformationId,
                    primaryPolicyHolderMemberId: ocrResults?.memberId ?? '',
                    payor: ocrResults.insuranceProviderName!,
                    primary: false,
                    subscriberId: selfSubscriberId,
                  };
                const subscribers = getCleanSubscribers(field.value, forms);

                const valueToSubmit: api.PrevisitPatientInsurance.PrevisitInsuranceFormFieldValue =
                  {
                    ...parsedValue,
                    subscribers,
                    insuranceCoverages: [
                      ...(parsedValue.insuranceCoverages || []),
                      insuranceCoverage,
                    ],
                  };
                await maybeUpdateInsuranceDetails(
                  valueToSubmit,
                  ocrResults!,
                  encounter.id,
                );

                await addInsuranceId(
                  api.PrevisitPatientInsurance.Footprint
                    .SubmittedSubscriberDetails,
                  insuranceCoverage,
                );
                await fetchInsuranceCards(selectedFormSetId);
                toast.open(t('toast'), { autoHideDuration: 5000 });
                history.replace('./review' + location.search);
              } else {
                propagateOcrState = true;
                history.replace(
                  `./${patientInsuranceInformationId}/subscriber` +
                    location.search,
                );
              }
            }

            setPictures({ [Side.Front]: null, [Side.Back]: null });
            setOcrState((prev) => ({
              ...(propagateOcrState ? prev : INITIAL_OCR_STATE),
              started: false,
              completed: true,
            }));
          }}
          ocrState={ocrState}
          setOcrState={setOcrState}
          setPatientInsuranceInformationId={setPatientInsuranceInformationId}
          setPatientUploadId={setPatientUploadId}
        />
      </Route>
      <Route path={`${match.path}/verify`}>
        <VerifyingInsurancePage
          rteState={rteState}
          setRteState={setRteState}
          onTimeout={async () => {
            await updateInsuranceFormValue({
              footprint: api.PrevisitPatientInsurance.Footprint.RteTimeout,
            });
          }}
          onComplete={async (
            patientInsuranceInformationId: string,
            patientUploadId: string,
            rteState: RteState,
          ) => {
            await fetchInsuranceCards(selectedFormSetId);
            await handleRteResponse(
              patientInsuranceInformationId,
              patientUploadId,
              rteState,
            );
          }}
          patientInsuranceInformationId={patientInsuranceInformationId}
          patientUploadId={patientUploadId}
          encounter={encounter}
        />
      </Route>
      <Route path={`${match.path}/error`}>
        <RteErrorReviewPage
          rteState={rteState}
          setRteState={setRteState}
          cards={cards}
          payorsDisplayNameMapping={payorsDisplayNameMapping}
          patientInsuranceInformationId={patientInsuranceInformationId!}
          onAddInsurance={() => history.replace('./upload' + location.search)}
          onCancel={async () => {
            await fetchInsuranceCards(selectedFormSetId);
            history.replace('.' + location.search);
          }}
        />
      </Route>
      <Route path={`${match.path}/:insuranceInformationId/edit`}>
        <EditInsurancePage
          cards={cards}
          payorsDisplayNameMapping={payorsDisplayNameMapping}
          encounterId={encounter.id}
          ocrResults={ocrState.results}
          field={field}
          onSubmit={(values) => submitEditInsurance(values)}
          onSkipInsurance={async () => {
            await updateInsuranceFormValue({
              skippedInsurance: true,
              footprint:
                api.PrevisitPatientInsurance.Footprint.SkippedInsurance,
            });

            await navigateToNextPage();
          }}
          onCancel={async () => {
            setOcrState(INITIAL_OCR_STATE);
            await updateInsuranceFormValue({
              footprint: api.PrevisitPatientInsurance.Footprint.CanceledAddCard,
            });
            if (patientUploadId && patientInsuranceInformationId) {
              await api.client.PatientUpload.invalidateCurrentDocument(
                patientUploadId,
              );
              await api.client.PrevisitPatientInsuranceInformation.remove({
                insuranceInformationId: patientInsuranceInformationId,
                encounterId: encounter.id,
              });
            }
            await fetchInsuranceCards(selectedFormSetId);
            // routing has different behavior when there is an existing record
            // vs when there isn't despite the url paths being identical
            history.replace(
              `${cards.length - 1 > 0 ? '.' : '..'}` + location.search,
            );
          }}
        />
      </Route>
      <Route path={`${match.path}/:insuranceInformationId/subscriber`}>
        <SubscriberPage
          cards={cards}
          encounterId={encounter.id}
          field={field}
          ocrResults={ocrState.results}
          onSubmit={submitSubscriberForm}
        />
      </Route>
      <Route path={`${match.path}/review`}>
        <ReviewInsurancePage
          cards={cards}
          payorsDisplayNameMapping={payorsDisplayNameMapping}
          navigateToNextPage={navigateToNextPage}
          onAddInsurance={() => history.replace('./upload' + location.search)}
          onContinue={async () => {
            await addConfirmedInsurances();
            await navigateToNextPage();
          }}
          onEdit={async (insuranceInformationId: string) => {
            await updateInsuranceFormValue({
              footprint:
                api.PrevisitPatientInsurance.Footprint
                  .EditingIncompleteInsurance,
            });
            navigateToEditPage(insuranceInformationId);
          }}
          onRemoveInsurance={handleRemoveInsurance}
          handleInsuranceChoice={handleInsuranceChoice}
        />
      </Route>
    </Switch>
  );
};

const mapStateToProps = (state: store.FormSetSelected): StateProps => {
  const selectedPracticePreference = getSelectedPracticePreference(state);

  return {
    cards: state.insuranceCards.data,
    patientAppRteEnabled: selectedPracticePreference.patientAppRteEnabled,
    selectedFormSetId: state.selectedFormSetId,
    forms: getSelectedForms(state.formSets, state.selectedFormSetId),
    exportInsuranceWithNullCoverageStartDateEnabled:
      selectedPracticePreference.exportInsuranceWithNullCoverageStartDateEnabled,
  };
};

const mapDispatchToProps = (dispatch: Function): DispatchProps => ({
  fetchInsuranceCards: makeFetchInsuranceCards(dispatch),
});

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(withTranslation('insurance')(InsuranceRouter)),
);
